import { useEffect, useState } from "react";
import { Reorder, useDragControls } from "framer-motion";
import { ArrowDown, ArrowUp, Check, Eye, EyeOff, GripVertical, Lock, RotateCcw } from "lucide-react";
import { api } from "@/lib/api";

const Row = ({ s, index, total, onToggle, onMove }) => {
  const controls = useDragControls();
  return (
    <Reorder.Item value={s} dragListener={false} dragControls={controls} className={s.enabled ? "layout-row" : "layout-row disabled"} data-testid={`layout-row-${s.key}`}>
      <span className="layout-grip" onPointerDown={e => controls.start(e)} data-testid={`layout-grip-${s.key}`}><GripVertical size={16} /></span>
      <span className="layout-order">{String(index + 1).padStart(2, "0")}</span>
      <div className="layout-label">
        <b>{s.label}</b>
        <small>{s.locked ? "Always visible" : s.enabled ? "Visible on homepage" : "Hidden from homepage"}</small>
      </div>
      <div className="layout-actions">
        <button type="button" disabled={index === 0} onClick={() => onMove(index, -1)} aria-label="Move up" data-testid={`layout-up-${s.key}`}><ArrowUp size={14} /></button>
        <button type="button" disabled={index === total - 1} onClick={() => onMove(index, 1)} aria-label="Move down" data-testid={`layout-down-${s.key}`}><ArrowDown size={14} /></button>
        {s.locked ? (
          <span className="layout-lock" data-testid={`layout-locked-${s.key}`}><Lock size={13} /></span>
        ) : (
          <button type="button" role="switch" aria-checked={s.enabled} className={s.enabled ? "switch on" : "switch"} onClick={() => onToggle(s.key)} data-testid={`layout-toggle-${s.key}`}>
            <i />{s.enabled ? <Eye size={11} /> : <EyeOff size={11} />}
          </button>
        )}
      </div>
    </Reorder.Item>
  );
};

export const HomepageLayout = () => {
  const [sections, setSections] = useState([]);
  const [status, setStatus] = useState("");
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = () => api("/admin/homepage-sections").then(d => { setSections(d.sections); setDirty(false); }).catch(e => setStatus(e.message));
  useEffect(() => { load(); }, []);

  const update = next => { setSections(next); setDirty(true); setStatus(""); };
  const toggle = key => update(sections.map(s => (s.key === key ? { ...s, enabled: !s.enabled } : s)));
  const move = (i, dir) => {
    const next = [...sections];
    const [item] = next.splice(i, 1);
    next.splice(i + dir, 0, item);
    update(next);
  };
  const save = async () => {
    setSaving(true); setStatus("");
    try {
      const d = await api("/admin/homepage-sections", { method: "PUT", body: JSON.stringify({ sections: sections.map(s => ({ key: s.key, enabled: s.enabled })) }) });
      setSections(d.sections); setDirty(false); setStatus("Homepage layout published.");
    } catch (e) { setStatus(e.message); }
    finally { setSaving(false); }
  };

  return (
    <div className="layout-panel" data-testid="homepage-layout-panel">
      <div className="layout-head">
        <p>Drag the handle or use the arrows to reorder. Toggle the switch to show or hide a section. Changes go live when you publish.</p>
        <div className="admin-toolbar-actions">
          <button className="button button-ghost dark" onClick={load} disabled={!dirty} data-testid="layout-reset-button"><RotateCcw size={14} /> Discard</button>
          <button className="button button-gold" onClick={save} disabled={!dirty || saving} data-testid="layout-save-button"><Check size={15} /> {saving ? "Publishing…" : "Publish layout"}</button>
        </div>
      </div>
      {status && <div className={status.includes("published") ? "success-message" : "error-message"} data-testid="layout-status">{status}</div>}
      <Reorder.Group axis="y" values={sections} onReorder={update} className="layout-list" as="div">
        {sections.map((s, i) => <Row key={s.key} s={s} index={i} total={sections.length} onToggle={toggle} onMove={move} />)}
      </Reorder.Group>
    </div>
  );
};
